import type { WarningNotice } from '../statementMeta';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { StatementSkeleton } from '../components';
import {
    BalanceCheckBar,
    StatementView,
    StatementWarnings,
} from '../components';
import type { StatementColumns, StatementRow } from '../statementMeta';
import type { BalanceCheckView } from './useBalanceSheetPage';

type BalanceSheetPageViewProps = {
    rows: StatementRow[];
    columns: StatementColumns;
    balance: BalanceCheckView | null;
    warningNotices: WarningNotice[];
    /** Postable accounts the layout maps nowhere — the statement is short. */
    unmappedCodes: string[];
    onDismissWarnings: () => void;
    onViewLedger: (accountId: string) => void;
    generatedText: string;
    /**
     * The statement is still in flight. The as-of control and Print keep their
     * real labels and are disabled; only the statement body skeletons.
     */
    isLoading: boolean;
};

/**
 * Balance Sheet — render only. The shell supplies the page title; this owns just
 * the as-of control, the prominent balance check, any advisory notices, and the
 * shared statement renderer (with prior-year comparatives + drill to ledger).
 */
export const BalanceSheetPageView: React.FC<BalanceSheetPageViewProps> = ({
    rows,
    columns,
    balance,
    warningNotices,
    unmappedCodes,
    onDismissWarnings,
    onViewLedger,
    generatedText,
    isLoading,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('statements.balanceSheet.title')}
            className="flex flex-col gap-4"
        >
            {/* Print is all that is left of the control row — the as-of date
                belongs to the Statements shell above. */}
            <div className="flex flex-wrap items-end justify-end gap-4 print:hidden">
                <Button
                    isDisabled={isLoading}
                    onPress={() => window.print()}
                    variant="outline"
                    {...getNodeTestIdProp('balance-sheet-print')}
                >
                    <Icon aria-hidden icon="gravity-ui:printer" />
                    {t('statements.print')}
                </Button>
            </div>

            {isLoading ? (
                <StatementSkeleton
                    showBalanceCheck
                    testIdPrefix="balance-sheet"
                />
            ) : (
                <>
                    {balance ? (
                        <BalanceCheckBar
                            balanced={balance.balanced}
                            differenceText={balance.differenceText}
                            leftLabel={balance.leftLabel}
                            leftText={balance.leftText}
                            rightLabel={balance.rightLabel}
                            rightText={balance.rightText}
                        />
                    ) : null}

                    <StatementWarnings
                        notices={warningNotices}
                        unmappedCodes={unmappedCodes}
                        onDismiss={onDismissWarnings}
                        testIdPrefix="balance-sheet"
                    />

                    <StatementView
                        ariaLabel={t('statements.balanceSheet.ariaLabel')}
                        footerNote={t('statements.footer.currencyNote')}
                        generatedText={t('statements.footer.generated', {
                            datetime: generatedText,
                        })}
                        columns={columns}
                        onViewLedger={onViewLedger}
                        rows={rows}
                        testIdPrefix="balance-sheet"
                    />
                </>
            )}
        </section>
    );
};
